import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  BookOpen, 
  Download, 
  Calendar, 
  FileText,
  Search,
  Loader2,
  Tag,
  Lock,
  ArrowRight,
  UserPlus,
  Newspaper
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = {
  press_release: { label: 'Press Release', color: 'bg-red-100 text-red-700' },
  monthly_update: { label: 'Monthly Update', color: 'bg-blue-100 text-blue-700' },
  quarterly_update: { label: 'Quarterly Update', color: 'bg-purple-100 text-purple-700' },
  news: { label: 'News', color: 'bg-green-100 text-green-700' },
  policy_brief: { label: 'Policy Brief', color: 'bg-orange-100 text-orange-700' },
  guidance_note: { label: 'Guidance Note', color: 'bg-teal-100 text-teal-700' },
  newsletter: { label: 'Newsletter', color: 'bg-pink-100 text-pink-700' }
};

const CATEGORIES = {
  circular_economy: 'Circular Economy',
  epr_updates: 'EPR Updates',
  policy_brief: 'Policy Brief',
  newsletter: 'Newsletter',
  guidance: 'Guidance'
};

export default function KnowledgeHub() {
  const { token, isAuthenticated } = useAuth();
  const [pressReleases, setPressReleases] = useState([]);
  const [memberContent, setMemberContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('press-releases');

  useEffect(() => {
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Always load public press releases
      await loadPressReleases();
      
      // Check access and load member content if authenticated
      if (isAuthenticated) {
        await checkAccessAndLoadMemberContent();
      } else {
        setHasAccess(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPressReleases = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/knowledge/public`);
      if (response.ok) {
        const data = await response.json();
        setPressReleases(data);
      }
    } catch (error) {
      console.error('Failed to load press releases:', error);
    }
  };

  const checkAccessAndLoadMemberContent = async () => {
    try {
      const accessResponse = await fetch(`${API_BASE}/api/knowledge/access-check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (accessResponse.ok) {
        const accessData = await accessResponse.json();
        setHasAccess(accessData.has_access);
        
        if (accessData.has_access) {
          const contentResponse = await fetch(`${API_BASE}/api/knowledge`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (contentResponse.ok) {
            const data = await contentResponse.json();
            // Filter out press releases as they're shown separately
            const nonPressReleases = data.filter(item => item.content_type !== 'press_release');
            setMemberContent(nonPressReleases);
          }
        }
      }
    } catch (error) {
      console.error('Failed to check access:', error);
      setHasAccess(false);
    }
  };

  const handleDownload = async (item, isPublic = false) => {
    if (isPublic) {
      window.open(`${API_BASE}/api/knowledge/public/${item.id}/attachment`, '_blank');
    } else {
      window.open(`${API_BASE}/api/knowledge/${item.id}/attachment`, '_blank');
    }
  };

  const filteredPressReleases = pressReleases.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const filteredMemberContent = memberContent.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || item.content_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#056839]" />
      </div>
    );
  }

  const renderContentCard = (item, isPublic = false) => (
    <div 
      key={item.id} 
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      data-testid={`content-card-${item.id}`}
    >
      <div className="p-6">
        {/* Type Badge */}
        <div className="flex items-center gap-2 mb-3">
          <Badge className={CONTENT_TYPES[item.content_type]?.color || 'bg-gray-100 text-gray-700'}>
            {CONTENT_TYPES[item.content_type]?.label || item.content_type}
          </Badge>
          {item.period_label && (
            <Badge variant="outline" className="text-[#6B7280]">
              {item.period_label}
            </Badge>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-[#056839] mb-2 line-clamp-2">
          {item.title}
        </h3>

        {/* Summary */}
        <p className="text-[#6B7280] text-sm mb-4 line-clamp-3">
          {item.summary}
        </p>

        {/* Meta */}
        <div className="flex items-center gap-4 text-xs text-[#6B7280] mb-4">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(item.publish_date)}
          </div>
          {item.category && (
            <div className="flex items-center gap-1">
              <Tag className="h-3 w-3" />
              {CATEGORIES[item.category] || item.category}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="flex-1"
            onClick={() => setSelectedItem({ ...item, isPublic })}
          >
            Read More
          </Button>
          {item.attachment && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleDownload(item, isPublic)}
              className="text-[#056839]"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="knowledge-hub-page">
      <SEO 
        title="Knowledge Hub | AfriCycle Zambia EPR Resources & News"
        description="Access AfriCycle Zambia's Knowledge Hub for EPR compliance resources, press releases, industry news, and member-exclusive content on plastic packaging and WEEE recycling in Zambia."
        keywords="AfriCycle knowledge hub, EPR resources Zambia, plastic recycling news, WEEE compliance guides, environmental regulations Africa, waste management resources"
        canonicalUrl="https://africycle.pro/knowledge-hub"
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D2E4D6] flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-[#056839]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#056839]">KNOWLEDGE HUB</h1>
              <p className="text-[#6B7280]">Stay updated with the latest EPR news and resources</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="press-releases" className="flex items-center gap-2" data-testid="press-releases-tab">
              <Newspaper className="h-4 w-4" />
              Press Releases
            </TabsTrigger>
            <TabsTrigger value="member-content" className="flex items-center gap-2" data-testid="member-content-tab">
              <BookOpen className="h-4 w-4" />
              Member Content
              {!hasAccess && <Lock className="h-3 w-3 ml-1" />}
            </TabsTrigger>
          </TabsList>

          {/* Press Releases Tab - Public */}
          <TabsContent value="press-releases">
            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                <Input
                  placeholder="Search press releases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-press-releases"
                />
              </div>
            </div>

            {/* Press Releases Grid */}
            {filteredPressReleases.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                <Newspaper className="h-12 w-12 text-[#D2E4D6] mx-auto mb-4" />
                <p className="text-[#6B7280]">No press releases available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPressReleases.map((item) => renderContentCard(item, true))}
              </div>
            )}
          </TabsContent>

          {/* Member Content Tab - Protected */}
          <TabsContent value="member-content">
            {hasAccess ? (
              <>
                {/* Filters */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                      <Input
                        placeholder="Search articles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-member-content"
                      />
                    </div>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-full md:w-[200px]">
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {Object.entries(CONTENT_TYPES)
                          .filter(([key]) => key !== 'press_release')
                          .map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Member Content Grid */}
                {filteredMemberContent.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                    <BookOpen className="h-12 w-12 text-[#D2E4D6] mx-auto mb-4" />
                    <p className="text-[#6B7280]">No member content available yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMemberContent.map((item) => renderContentCard(item, false))}
                  </div>
                )}
              </>
            ) : (
              /* Access Denied for Member Content */
              <div className="bg-[#D2E4D6]/30 rounded-xl border border-[#D2E4D6] p-12 text-center" data-testid="member-content-locked">
                <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <Lock className="h-10 w-10 text-[#056839]" />
                </div>
                <h2 className="text-2xl font-bold text-[#056839] mb-4">MEMBER-ONLY CONTENT</h2>
                <p className="text-[#6B7280] mb-8 text-lg max-w-md mx-auto">
                  Access to industry updates, policy insights, and resources is available to registered members with an active compliance package.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button className="btn-primary w-full sm:w-auto" data-testid="register-cta-btn">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Register with us
                    </Button>
                  </Link>
                  <Link to="/services">
                    <Button variant="outline" className="btn-outline w-full sm:w-auto" data-testid="view-packages-cta-btn">
                      View Compliance Packages
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Content Detail Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={CONTENT_TYPES[selectedItem.content_type]?.color || 'bg-gray-100 text-gray-700'}>
                    {CONTENT_TYPES[selectedItem.content_type]?.label || selectedItem.content_type}
                  </Badge>
                  {selectedItem.period_label && (
                    <Badge variant="outline">{selectedItem.period_label}</Badge>
                  )}
                </div>
                <DialogTitle className="text-xl text-[#056839]">{selectedItem.title}</DialogTitle>
                <div className="flex items-center gap-4 text-sm text-[#6B7280] mt-2">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedItem.publish_date)}
                  </div>
                  {selectedItem.category && (
                    <div className="flex items-center gap-1">
                      <Tag className="h-4 w-4" />
                      {CATEGORIES[selectedItem.category] || selectedItem.category}
                    </div>
                  )}
                </div>
              </DialogHeader>

              <div className="mt-4">
                {/* Summary */}
                <div className="bg-[#D2E4D6]/30 rounded-lg p-4 mb-6">
                  <p className="text-[#3F3F3F] font-medium">{selectedItem.summary}</p>
                </div>

                {/* Body */}
                {selectedItem.body && (
                  <div className="prose prose-sm max-w-none text-[#3F3F3F]">
                    <div dangerouslySetInnerHTML={{ __html: selectedItem.body.replace(/\n/g, '<br/>') }} />
                  </div>
                )}

                {/* Attachment */}
                {selectedItem.attachment && (
                  <div className="mt-6 p-4 bg-[#F9FAFB] rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-[#3F3F3F]">{selectedItem.attachment.filename}</p>
                          <p className="text-xs text-[#6B7280]">PDF Document</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => handleDownload(selectedItem, selectedItem.isPublic)} 
                        className="btn-primary"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
